import { StakeholderModel } from '../model/StakeholderModel';
import {
	radarOriginalSize,
	radarBoundariesRatio,
	radarHighToAverageBoundariesRatio,
	radarAverageToLowBoundariesRatio,
	radarLikelyToUndefinedBoundariesRatio
} from './radarBoundaries';
import { isItemNeutral } from './rules';

// Types pour les paramètres de la fonction
export type StakeholderSlice =
	| 'inactive'
	| 'low'
	| 'medium'
	| 'high'
	| 'neutral';
export type StakeholderPolarity = 'positive' | 'negative';

// Configuration du radar basée sur le système existant
interface RadarConfig {
	originalSize: number;
	boundariesRatio: number;
	highToAverageRatio: number;
	averageToLowRatio: number;
	likelyToUndefinedRatio: number;
	minDistance: number; // Distance minimale entre les stakeholders en coordonnées normalisées
}

// Rayon absolu maximum autorisé en coordonnées normalisées (|r| <= 120)
const ABSOLUTE_MAX_RADIUS = 120;

// Position optimale calculée (coordonnées normalisées du radar)
export interface OptimalPosition {
	x: number; // Coordonnée X normalisée (-100 à +100)
	y: number; // Coordonnée Y normalisée (-100 à +100)
	angle?: number; // Angle en radians pour les demi-cercles
	distance?: number; // Distance du centre pour les demi-cercles
}

// Configuration par défaut basée sur le système de radar existant
const DEFAULT_CONFIG: RadarConfig = {
	originalSize: radarOriginalSize,
	boundariesRatio: radarBoundariesRatio,
	highToAverageRatio: radarHighToAverageBoundariesRatio,
	averageToLowRatio: radarAverageToLowBoundariesRatio,
	likelyToUndefinedRatio: radarLikelyToUndefinedBoundariesRatio,
	minDistance: 15 // Distance minimale en coordonnées normalisées
};

/**
 * Calcule la distance euclidienne entre deux points
 */
function calculateDistance(
	x1: number,
	y1: number,
	x2: number,
	y2: number
): number {
	return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

/**
 * Calcule la distance minimale entre un point et tous les stakeholders existants
 */
function calculateMinDistanceToStakeholders(
	x: number,
	y: number,
	existingStakeholders: StakeholderModel[]
): number {
	if (existingStakeholders.length === 0) return Infinity;

	return Math.min(
		...existingStakeholders.map((stakeholder) =>
			calculateDistance(x, y, stakeholder.x, stakeholder.y)
		)
	);
}

/**
 * Calcule les limites de rayon pour chaque slice basées sur le système de radar existant
 */
function getSliceRadiusLimits(
	slice: StakeholderSlice,
	config: RadarConfig
): { minRadius: number; maxRadius: number } {
	const normalizedRadius = 100; // Rayon maximum en coordonnées normalisées

	switch (slice) {
		case 'high':
			return {
				minRadius: 0,
				maxRadius: Math.min(
					normalizedRadius * config.highToAverageRatio,
					normalizedRadius
				)
			};
		case 'medium':
			return {
				minRadius: normalizedRadius * config.highToAverageRatio,
				maxRadius: Math.min(
					normalizedRadius * config.averageToLowRatio,
					normalizedRadius
				)
			};
		case 'low':
			return {
				minRadius: normalizedRadius * config.averageToLowRatio,
				maxRadius: normalizedRadius // S'arrêter à 100% au lieu d'utiliser likelyToUndefinedRatio
			};
		case 'inactive':
			// Les stakeholders inactive sont placés AU-DELÀ du cercle principal (> 100)
			// Zone bornée pour ne pas dépasser ±120
			return {
				minRadius: Math.min(ABSOLUTE_MAX_RADIUS, normalizedRadius + 2),
				maxRadius: ABSOLUTE_MAX_RADIUS
			};
		default:
			return { minRadius: 0, maxRadius: normalizedRadius };
	}
}

/**
 * Trouve la meilleure position pour un stakeholder dans un demi-cercle
 */
function findBestPositionInSemicircle(
	slice: StakeholderSlice,
	polarity: StakeholderPolarity,
	existingStakeholders: StakeholderModel[],
	config: RadarConfig
): OptimalPosition {
	const { minRadius, maxRadius } = getSliceRadiusLimits(slice, config);

	// Définir les limites d'angle pour le demi-cercle en évitant la zone neutre
	// La zone neutre correspond à y proche de 0, donc on évite les angles proches de 0 et π
	// Marge élargie pour la zone neutre épaissie
	const neutralZoneMargin = slice === 'high' ? 8 : 15;
	const neutralZoneAngle = Math.asin(neutralZoneMargin / maxRadius);
	const startAngle =
		polarity === 'positive' ? neutralZoneAngle : Math.PI + neutralZoneAngle;
	const endAngle =
		polarity === 'positive'
			? Math.PI - neutralZoneAngle
			: 2 * Math.PI - neutralZoneAngle;

	// Si aucun stakeholder n'est encore positionné dans la même tranche et polarité,
	// placer le premier au CENTRE de la zone (milieu du rayon et de l'angle)
	const sameSliceStakeholders = existingStakeholders.filter((s) => {
		const sSlice = getStakeholderSliceFromPosition(s.x, s.y, config);
		const sPolarity = getStakeholderPolarityFromPosition(s.x, s.y);
		return sSlice === slice && sPolarity === polarity;
	});

	if (sameSliceStakeholders.length === 0) {
		// Placer le premier stakeholder au CENTRE du rayon de la tranche
		// (milieu entre minRadius et maxRadius, en gardant une petite marge)
		const margin = slice === 'inactive' ? 8 : 5;
		const cappedMaxRadius = Math.min(maxRadius, ABSOLUTE_MAX_RADIUS);
		const midRadius = (minRadius + margin + cappedMaxRadius - margin) / 2;
		const midAngle = (startAngle + endAngle) / 2;
		const x = Math.cos(midAngle) * midRadius;
		const y = Math.sin(midAngle) * midRadius;
		// S'assurer qu'on évite la zone neutre
		if (Math.abs(y) >= neutralZoneMargin) {
			return { x, y, angle: midAngle, distance: midRadius };
		}
		// Sinon, décale légèrement l'angle vers l'intérieur du demi-cercle
		const adjustedAngle =
			polarity === 'positive'
				? midAngle + neutralZoneAngle / 2
				: midAngle - neutralZoneAngle / 2;
		const adjX = Math.cos(adjustedAngle) * midRadius;
		const adjY = Math.sin(adjustedAngle) * midRadius;
		return { x: adjX, y: adjY, angle: adjustedAngle, distance: midRadius };
	}

	let bestPosition: OptimalPosition = { x: 0, y: 0, angle: 0, distance: 0 };
	let maxMinDistance = 0;

	// Utiliser le rayon maximum complet pour tous les slices, borné à ABSOLUTE_MAX_RADIUS
	const effectiveMaxRadius = Math.min(maxRadius, ABSOLUTE_MAX_RADIUS);

	// Tester différentes positions en parcourant l'angle et la distance
	const angleSteps = 36; // 36 pas de 10 degrés
	const radiusSteps = 5; // Même nombre d'étapes pour tous les slices

	for (let i = 0; i < angleSteps; i++) {
		const angle =
			startAngle + (endAngle - startAngle) * (i / (angleSteps - 1));

		for (let j = 0; j < radiusSteps; j++) {
			// Placer les stakeholders avec une marge pour éviter les bords
			const margin = slice === 'inactive' ? 8 : 5;
			const radiusRatio = (j / (radiusSteps - 1)) * 0.8 + 0.1; // Entre 10% et 90% du rayon
			const maxAllowedRadius = effectiveMaxRadius - margin;
			const minAllowedRadius = minRadius + margin;

			const distance =
				minAllowedRadius + (maxAllowedRadius - minAllowedRadius) * radiusRatio;
			const x = Math.cos(angle) * distance;
			const y = Math.sin(angle) * distance;

			// Éviter la zone neutre pour les slices non-neutres
			// Utiliser la même marge que pour le calcul des angles
			if (Math.abs(y) < neutralZoneMargin) {
				continue;
			}

			const minDistance = calculateMinDistanceToStakeholders(
				x,
				y,
				existingStakeholders
			);

			if (minDistance > maxMinDistance) {
				maxMinDistance = minDistance;
				bestPosition = { x, y, angle, distance };
			}
		}
	}

	return bestPosition;
}

/**
 * Trouve la meilleure position pour un stakeholder dans la zone neutre
 */
function findBestPositionInNeutralZone(
	existingStakeholders: StakeholderModel[]
): OptimalPosition {
	// Exigence: y = 0, x dans [-100, 100], optimisation basée sur les distances
	const minX = -100;
	const maxX = 100;
	const y = 0; // Toujours y = 0 pour les neutres

	let bestPosition: OptimalPosition = { x: 0, y: 0 };
	let maxMinDistance = -Infinity;

	// Si aucun stakeholder existant, placer au centre
	if (existingStakeholders.length === 0) {
		return { x: 0, y: 0 };
	}

	// Tester différentes positions sur l'axe horizontal avec un pas fin
	const xSteps = 201; // Pas de 1 unité sur [-100, 100]
	for (let i = 0; i < xSteps; i++) {
		const x = minX + (maxX - minX) * (i / (xSteps - 1));

		const minDist = calculateMinDistanceToStakeholders(
			x,
			y,
			existingStakeholders
		);

		if (minDist > maxMinDistance) {
			maxMinDistance = minDist;
			bestPosition = { x, y };
		}
	}

	return bestPosition;
}

/**
 * Fonction principale pour calculer la position optimale d'un stakeholder
 */
export function calculateOptimalStakeholderPosition(
	stakeholders: StakeholderModel[],
	slice: StakeholderSlice,
	polarity: StakeholderPolarity,
	config: Partial<RadarConfig> = {}
): OptimalPosition {
	const finalConfig = { ...DEFAULT_CONFIG, ...config };
	let relevantStakeholders: StakeholderModel[] = [];

	switch (slice) {
		case 'neutral':
			relevantStakeholders = stakeholders.filter((stakeholder) =>
				isItemNeutral(stakeholder)
			);
			return findBestPositionInNeutralZone(relevantStakeholders);

		case 'inactive':
		case 'low':
		case 'medium':
			relevantStakeholders = stakeholders.filter((stakeholder) => {
				const stakeholderSlice = getStakeholderSliceFromPosition(
					stakeholder.x,
					stakeholder.y,
					finalConfig
				);
				const stakeholderPolarity = getStakeholderPolarityFromPosition(
					stakeholder.x,
					stakeholder.y
				);
				return (
					stakeholderSlice === slice &&
					stakeholderPolarity === polarity
				);
			});
			return findBestPositionInSemicircle(
				slice,
				polarity,
				relevantStakeholders,
				finalConfig
			);

		case 'high':
			relevantStakeholders = stakeholders.filter((stakeholder) => {
				const stakeholderSlice = getStakeholderSliceFromPosition(
					stakeholder.x,
					stakeholder.y,
					finalConfig
				);
				const stakeholderPolarity = getStakeholderPolarityFromPosition(
					stakeholder.x,
					stakeholder.y
				);
				return (
					stakeholderSlice === slice &&
					stakeholderPolarity === polarity
				);
			});
			return findBestPositionInSemicircle(
				slice,
				polarity,
				relevantStakeholders,
				finalConfig
			);

		default:
			throw new Error(`Slice non supporté: ${slice}`);
	}
}

/**
 * Fonction utilitaire pour mettre à jour la position d'un stakeholder
 */
export function updateStakeholderPosition(
	stakeholder: StakeholderModel,
	position: OptimalPosition
): StakeholderModel {
	return {
		...stakeholder,
		x: position.x,
		y: position.y
	};
}

/**
 * Fonction pour optimiser les positions de tous les stakeholders d'une tranche
 */
export function optimizeStakeholderPositionsInSlice(
	stakeholders: StakeholderModel[],
	slice: StakeholderSlice,
	polarity: StakeholderPolarity,
	config: Partial<RadarConfig> = {}
): StakeholderModel[] {
	const finalConfig = { ...DEFAULT_CONFIG, ...config };
	const updatedStakeholders = [...stakeholders];

	// Trier les stakeholders par priorité (par exemple, par date de création)
	updatedStakeholders.sort(
		(a, b) =>
			new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
	);

	// Optimiser chaque stakeholder un par un
	for (let i = 0; i < updatedStakeholders.length; i++) {
		const currentStakeholder = updatedStakeholders[i];
		const otherStakeholders = updatedStakeholders.filter(
			(_, index) => index !== i
		);

		const optimalPosition = calculateOptimalStakeholderPosition(
			otherStakeholders,
			slice,
			polarity,
			finalConfig
		);

		updatedStakeholders[i] = updateStakeholderPosition(
			currentStakeholder,
			optimalPosition
		);
	}

	return updatedStakeholders;
}

/**
 * Fonction pour optimiser les positions des stakeholders neutres en tenant compte de tous les autres stakeholders
 */
function optimizeNeutralStakeholderPositions(
	neutralStakeholders: StakeholderModel[],
	_allOtherStakeholders: StakeholderModel[]
): StakeholderModel[] {
	// Approche: placement séquentiel déterministe (par id),
	// maximisant la distance aux stakeholders déjà placés (neutres précédents + autres existants).
	if (!neutralStakeholders || neutralStakeholders.length === 0) return [];

	const sorted = [...neutralStakeholders].sort((a, b) => {
		const ak = (a.id ?? a.name ?? '').toString();
		const bk = (b.id ?? b.name ?? '').toString();
		return ak.localeCompare(bk);
	});

	const placed: StakeholderModel[] = [];
	for (let i = 0; i < sorted.length; i++) {
		const current = sorted[i];

		// Contraintes: tous les déjà placés (neutres) + tous les autres existants
		const indicators: StakeholderModel[] = [
			...placed,
			..._allOtherStakeholders
		];

		const optimal = findBestPositionInNeutralZone(indicators);
		const updated = updateStakeholderPosition(current, optimal);
		placed.push(updated);
	}

	return placed;
}

/**
 * Fonction utilitaire pour convertir les coordonnées normalisées en coordonnées pixels
 * Utilise la logique de dénormalisation du radar existant
 */
export function denormalizeRadarCoordinates(
	x: number,
	y: number,
	width: number,
	height: number,
	offsetX: number = 0,
	offsetY: number = 0
): [number, number] {
	const normalizerRatio = ((1 / width) * 200) / radarBoundariesRatio;

	return [
		x / normalizerRatio + width / 2 + offsetX,
		-y / normalizerRatio + height / 2 + offsetY
	];
}

/**
 * Fonction utilitaire pour convertir les coordonnées pixels en coordonnées normalisées
 * Utilise la logique de normalisation du radar existant
 */
export function normalizeRadarCoordinates(
	x: number,
	y: number,
	width: number,
	height: number,
	offsetX: number = 0,
	offsetY: number = 0
): [number, number] {
	const normalizerRatio = ((1 / width) * 200) / radarBoundariesRatio;

	x = x - offsetX;
	y = y - offsetY;

	return [
		(x - width / 2) * normalizerRatio,
		-(y - height / 2) * normalizerRatio
	];
}

/**
 * Fonction pour déterminer la tranche d'un stakeholder basée sur sa position
 */
export function getStakeholderSliceFromPosition(
	x: number,
	y: number,
	config: RadarConfig = DEFAULT_CONFIG
): StakeholderSlice {
	const distance = Math.sqrt(x * x + y * y);
	const normalizedRadius = 100;

	// Utiliser la fonction existante isItemNeutral pour déterminer si un stakeholder est neutre
	if (isItemNeutral({ x, y })) {
		return 'neutral';
	}

	if (distance <= normalizedRadius * config.highToAverageRatio) {
		return 'high';
	} else if (distance <= normalizedRadius * config.averageToLowRatio) {
		return 'medium';
	} else if (distance <= normalizedRadius) {
		return 'low';
	} else {
		return 'inactive';
	}
}

/**
 * Fonction pour déterminer la polarité d'un stakeholder basée sur sa position
 */
export function getStakeholderPolarityFromPosition(
	_x: number, // Non utilisé mais gardé pour la cohérence de l'interface
	y: number
): StakeholderPolarity {
	const polarity = y > 0 ? 'positive' : 'negative';
	return polarity;
}

/**
 * Fonction pour optimiser toutes les positions des stakeholders d'un projet
 */
export function optimizeAllStakeholderPositions(
	stakeholders: StakeholderModel[],
	config: Partial<RadarConfig> = {}
): StakeholderModel[] {
	const finalConfig = { ...DEFAULT_CONFIG, ...config };
	const updatedStakeholders = [...stakeholders];

	// Grouper les stakeholders par tranche et polarité
	const groupedStakeholders = new Map<string, StakeholderModel[]>();

	updatedStakeholders.forEach((stakeholder) => {
		const slice = getStakeholderSliceFromPosition(
			stakeholder.x,
			stakeholder.y,
			finalConfig
		);

		// Pour les neutres, utiliser une clé unique sans polarité
		const key =
			slice === 'neutral'
				? 'neutral'
				: `${slice}-${getStakeholderPolarityFromPosition(stakeholder.x, stakeholder.y)}`;

		if (!groupedStakeholders.has(key)) {
			groupedStakeholders.set(key, []);
		}
		groupedStakeholders.get(key)!.push(stakeholder);
	});

	// Optimiser chaque groupe en tenant compte des autres groupes
	const result: StakeholderModel[] = [];
	const allOtherStakeholders: StakeholderModel[] = [];

	// Traiter d'abord les groupes non-neutres pour éviter qu'ils interfèrent avec la zone neutre
	const nonNeutralGroups = Array.from(groupedStakeholders.entries()).filter(
		([key]) => key !== 'neutral'
	);
	const neutralGroups = Array.from(groupedStakeholders.entries()).filter(
		([key]) => key === 'neutral'
	);

	// Optimiser les groupes non-neutres d'abord
	nonNeutralGroups.forEach(([, groupStakeholders]) => {
		const [slice, polarity] = groupStakeholders[0]
			? [
					getStakeholderSliceFromPosition(
						groupStakeholders[0].x,
						groupStakeholders[0].y,
						finalConfig
					),
					getStakeholderPolarityFromPosition(
						groupStakeholders[0].x,
						groupStakeholders[0].y
					)
				]
			: (['low', 'positive'] as [StakeholderSlice, StakeholderPolarity]);

		const optimizedGroup = optimizeStakeholderPositionsInSlice(
			groupStakeholders,
			slice,
			polarity,
			finalConfig
		);
		result.push(...optimizedGroup);
		allOtherStakeholders.push(...optimizedGroup);
	});

	// Puis optimiser les groupes neutres en tenant compte des autres stakeholders
	neutralGroups.forEach(([, groupStakeholders]) => {
		// Pour les stakeholders neutres, optimiser en tenant compte de tous les autres stakeholders
		const optimizedGroup = optimizeNeutralStakeholderPositions(
			groupStakeholders,
			allOtherStakeholders
		);
		result.push(...optimizedGroup);
	});

	return result;
}
